import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLang } from '../i18n.jsx';
import { api } from '../api.js';
import { useCierreSuave, useFocoModal, useVolverCierra } from '../movimiento.js';
import { Cargando } from './Decorado.jsx';
import Bandera from './Bandera.jsx';

/**
 * Perfil de jugador, abierto desde la clasificacion de un torneo.
 *
 * Todo sale de los standings que ya se ingieren: record global, top 8, mazo
 * habitual y resultados recientes. Es la pieza que convierte un nombre suelto
 * de la tabla en alguien con historia -- y la respuesta a "¿este que gano es
 * bueno o tuvo un buen dia?". Mismo esqueleto de modal que el resto de la
 * casa: portal, cierre suave, "atras" cierra, Escape cierra.
 */
export default function PlayerCard({ player, onClose, onOpenList }) {
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  const { ref, cerrando, pedirCierre } = useCierreSuave(onClose);
  useFocoModal(ref);
  useVolverCierra(true, pedirCierre);

  useEffect(() => {
    let alive = true;
    setData(null);
    api(`/api/meta/players/${encodeURIComponent(player)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setData(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [player]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') pedirCierre();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const fecha = (d) =>
    new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(
      lang === 'es' ? 'es-CL' : 'en-US',
      { day: 'numeric', month: 'short' }
    );

  return createPortal(
    <div className={`overlay overlay--perfil${cerrando ? ' is-closing' : ''}`} onClick={pedirCierre}>
      <div
        ref={ref}
        className="detail perfil"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={data?.name ?? player}
        aria-busy={!data}
      >
        <button type="button" className="detail__close" onClick={pedirCierre} aria-label={t('close')}>
          ×
        </button>
        {!data ? (
          <Cargando texto={t('loading')} />
        ) : (
          <>
            <header className="perfil__cab">
              <h2>
                <Bandera cc={data.country} className="perfil__pais" />
                {data.name}
              </h2>
              <p className="muted perfil__cifras">
                {data.tournaments} {t('perfilTournaments')}
                {' · '}
                {t('perfilRecord')}{' '}
                <strong>
                  {data.wins}-{data.losses}
                  {data.ties ? `-${data.ties}` : ''}
                </strong>
                {data.top8 > 0 && (
                  <>
                    {' · '}
                    {data.top8}× {t('perfilTop8')}
                  </>
                )}
              </p>
              {data.favoriteDeck && (
                <p className="perfil__fav">
                  {t('perfilFavDeck')}: <strong>{data.favoriteDeck}</strong>
                </p>
              )}
            </header>

            <h3 className="perfil__rotulo">{t('perfilRecent')}</h3>
            <ul className="perfil__lista">
              {data.results.map((r) => (
                <li key={`${r.tournamentId}`} className="perfil__fila">
                  <span className="perfil__fecha muted">{fecha(r.date)}</span>
                  <span className="perfil__torneo" title={r.tournament}>{r.tournament}</span>
                  <span className="perfil__puesto">
                    {/*
                      AQUI el podio SI vale: `r.placing` es como termino ese
                      torneo, un resultado y no un indice de fila. Es el mismo
                      distintivo que la clasificacion, y por eso se reusa la
                      clase -- un tercer puesto se ve igual se mire donde se
                      mire.
                    */}
                    {r.placing == null
                      ? '—'
                      : r.placing <= 3
                        ? /* Con medalla, el texto NO repite el puesto: el
                             numero ya esta dentro del circulo y escribirlo
                             otra vez daba "1 1.º de 137". */
                          (
                            <>
                              <span className="podio podio--mini" data-puesto={r.placing}>
                                {r.placing}
                              </span>
                              {t('perfilOutOf', { n: r.fieldSize })}
                            </>
                          )
                        : t('perfilPlaceOf', { p: r.placing, n: r.fieldSize })}
                  </span>
                  {/*
                    EL RECORD DE ESE TORNEO, que el servidor ya mandaba y no se
                    pintaba: es "la forma reciente" del brief. Un 7-3 dice algo
                    que el puesto solo no dice -- un 32º de 400 con 7-3 no es el
                    mismo torneo que un 32º de 40 con 2-4.

                    En palabras y no en marcas de color: la regla de la casa
                    prohibe que el color sea la unica señal, y una tira de
                    puntitos verdes y rojos seria justo eso. El guion tambien
                    es mas corto de leer que seis circulos.
                  */}
                  <span className="perfil__mazo muted">
                    <span className="perfil__record">
                      {r.wins != null ? `${r.wins}-${r.losses}${r.ties ? `-${r.ties}` : ''}` : '—'}
                    </span>
                    {r.deckName ?? '—'}
                  </span>
                  {r.hasList && onOpenList && (
                    <button
                      type="button"
                      className="btn btn--ghost tour__ver"
                      onClick={() => onOpenList(r)}
                    >
                      {t('tourViewList')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
